import type {
  NewHomeSection,
  NewHomeSectionItem,
  NewHomeSectionValue,
  NewHomeSectionLayout,
  NewHomeSectionStyle,
  NewHomeTitleStyle,
  NewHomeHeroVariant,
} from '../../domain/models/NewHomePageData';
import * as sessionStore from '@/core/services/storage/utils/sessionStorageUtil';

const HERO_VARIANT_KEY = 'newHome:heroVariant';

/**
 * Application ViewModels — business-level transformations
 * Domain → App VM (filter, sort, resolve A/B variant)
 */

export interface SectionItemAppVm {
  position: number;
  title: string;
  titleDesktop?: string;
  imageUrl: string;
  dImageUrl?: string;
  imageUrlDesktop?: string;
  videoUrl?: string;
  posterUrl?: string;
  showVideo?: boolean;
  showSlider?: boolean;
  sliderImageUrls?: string[];
  dSliderImageUrls?: string[];
  sliderOrientation?: 'horizontal' | 'vertical';
  sliderAutoPlay?: boolean;
  sliderInterval?: number;
  link: string;
  linkDesktop?: string;
  showInMobile?: boolean;
  showInDesktop?: boolean;
  videoMobileOnly?: boolean;
  showSliderOnDesktop?: boolean;
  categoryName?: string;
  collection?: string;
  btn?: string;
  lazyload?: string;
  shouldRefresh?: boolean;
  productIds?: string[];
  overlayTextColor?: string;
  overlayBgColor?: string;
  bgColor?: string;
}

export interface HeroValueAppVm {
  isGradient: boolean;
  isVideo: boolean;
  /** Resolved hero media box height */
  height?: string;
  /** Resolved max height for hero media */
  maxHeight?: string;
  title: string[];
  bgUrl: string;
  posterUrl: string;
  fwImageUrl: string;
  fwImageBgUrl?: string;
  blankImageUrl: string;
  fwImagePosition?: string;
  fwImageWidth?: string;
  fwImageWidthDesktop?: string;
  link: string;
  shouldRefresh: boolean;
  productIds: string[];
  rgbColor?: string;
  hexColor?: string;
  autoplay?: string;
  playsinline?: string;
  loop?: string;
  muted?: string;
  layout: NewHomeSectionLayout;
  style?: NewHomeSectionStyle;
}

export interface GridSectionAppVm {
  title: string[];
  showTitle: boolean;
  titleStyle?: NewHomeTitleStyle;
  layout: NewHomeSectionLayout;
  style?: NewHomeSectionStyle;
  gridLayout?: string;
  dGridLayout?: string;
  blankImageUrl?: string;
  items: SectionItemAppVm[];
}

export interface CarouselSectionAppVm {
  title: string[];
  showTitle: boolean;
  titleStyle?: NewHomeTitleStyle;
  layout: NewHomeSectionLayout;
  style?: NewHomeSectionStyle;
  slideInterval?: number;
  blankImageUrl?: string;
  items: SectionItemAppVm[];
  /** Reels (`layout.template === 'reels'`) */
  desktopVisibleCount?: number;
  desktopPeekFraction?: number;
  /** Present when `layout.template === 'parallax-carousel'` */
  activeBasis?: string | number;
  inactiveBasis?: string | number;
  nextBasis?: string | number;
  activeBasisSm?: string | number;
  inactiveBasisSm?: string | number;
  nextBasisSm?: string | number;
  activeBasisLg?: string | number;
  inactiveBasisLg?: string | number;
  nextBasisLg?: string | number;
  snapOnClick?: boolean;
  parallaxFactor?: number;
  parallaxClamp?: number;
  /** Parallax: show arrows on mobile; default true */
  showParallaxArrowsOnMobile?: boolean;
}

export interface RecommendedSectionAppVm {
  title: string[];
  showTitle: boolean;
  titleStyle?: NewHomeTitleStyle;
  layout: NewHomeSectionLayout;
  style?: NewHomeSectionStyle;
  browseTags?: string;
  prefetchOffsetSections?: number;
  categoryFilters?: Array<{ label: string; tag: string; imageUrl: string }>;
}

export interface NewHomeSectionAppVm {
  priority: number;
  key: string;
  eventLabel: string;
  showInMobile: boolean;
  showInDesktop: boolean;
  value: HeroValueAppVm | GridSectionAppVm | CarouselSectionAppVm | RecommendedSectionAppVm;
}

/**
 * Domain → App ViewModel conversions
 */

function toSectionItemAppVm(item: NewHomeSectionItem): SectionItemAppVm {
  return {
    position: item.position,
    title: item.title,
    imageUrl: item.imageUrl,
    dImageUrl: item.dImageUrl,
    imageUrlDesktop: item.imageUrlDesktop,
    videoUrl: item.videoUrl,
    posterUrl: item.posterUrl,
    showVideo: item.showVideo,
    showSlider: item.showSlider,
    sliderImageUrls: item.sliderImageUrls,
    dSliderImageUrls: item.dSliderImageUrls,
    sliderOrientation: item.sliderOrientation,
    sliderAutoPlay: item.sliderAutoPlay,
    sliderInterval: item.sliderInterval,
    link: item.link,
    linkDesktop: item.linkDesktop,
    showInMobile: item.showInMobile,
    showInDesktop: item.showInDesktop,
    videoMobileOnly: item.videoMobileOnly,
    showSliderOnDesktop: item.showSliderOnDesktop,
    categoryName: item.categoryName,
    collection: item.collection,
    btn: item.btn,
    lazyload: item.lazyload,
    shouldRefresh: item.shouldRefresh,
    productIds: item.productIds,
    overlayTextColor: item.overlayTextColor,
    overlayBgColor: item.overlayBgColor,
    bgColor: item.bgColor,
  };
}

/**
 * Same weighted buckets as random pick, but seeded from config keys so server HTML matches client hydration.
 * Math.random() would diverge between SSR and the browser on first paint.
 */
function pickWeightedByStableSeed(entries: [string, NewHomeHeroVariant][]): [string, NewHomeHeroVariant] {
  const sorted = [...entries].sort(([a], [b]) => a.localeCompare(b));
  const totalWeight = sorted.reduce((sum, [, v]) => sum + v.weight, 0);
  if (sorted.length === 0) {
    const fallback = entries[0];
    return fallback ?? ['', {} as NewHomeHeroVariant];
  }
  if (totalWeight <= 0) {
    return sorted[0];
  }
  const seedStr = sorted.map(([k]) => k).join('|');
  let hash = 0;
  for (let i = 0; i < seedStr.length; i++) {
    hash = (hash * 31 + seedStr.charCodeAt(i)) >>> 0;
  }
  let pick = hash % totalWeight;
  for (const [key, variant] of sorted) {
    pick -= variant.weight;
    if (pick < 0) {
      return [key, variant];
    }
  }
  return sorted[sorted.length - 1];
}

/**
 * Weighted variant selection for A/B testing.
 * Persists the chosen variant key in sessionStorage so the user sees the same variant for the session.
 */
function selectVariant(variants: Record<string, NewHomeHeroVariant>): NewHomeHeroVariant {
  const entries = Object.entries(variants);

  const storedKey =
    typeof window !== 'undefined' ? sessionStore.getItem<string>(HERO_VARIANT_KEY) : null;
  if (storedKey && variants[storedKey]) {
    return variants[storedKey];
  }

  const [chosenKey, chosenVariant] = pickWeightedByStableSeed(entries);

  if (typeof window !== 'undefined') {
    sessionStore.setItem(HERO_VARIANT_KEY, chosenKey);
  }

  return chosenVariant;
}

function toHeroValueAppVm(value: NewHomeSectionValue): HeroValueAppVm {
  // If variants exist, select one based on weight
  if (value.variants && Object.keys(value.variants).length > 0) {
    const selected = selectVariant(value.variants);
    return {
      isGradient: selected.isGradient,
      isVideo: selected.isVideo,
      height: selected.height ?? value.height,
      maxHeight: selected.maxHeight ?? value.maxHeight,
      title: selected.title,
      bgUrl: selected.bgUrl,
      posterUrl: selected.posterUrl,
      fwImageUrl: selected.fwImageUrl,
      fwImageBgUrl: selected.fwImageBgUrl,
      blankImageUrl: selected.blankImageUrl,
      fwImagePosition: selected.fwImagePosition,
      fwImageWidth: selected.fwImageWidth,
      fwImageWidthDesktop: selected.fwImageWidthDesktop,
      link: selected.link,
      shouldRefresh: selected.shouldRefresh,
      productIds: selected.productIds,
      rgbColor: value.rgbColor,
      hexColor: value.hexColor,
      autoplay: value.autoplay,
      playsinline: value.playsinline,
      loop: value.loop,
      muted: value.muted,
      layout: value.layout,
      style: value.style,
    };
  }

  return {
    isGradient: value.isGradient ?? false,
    isVideo: value.isVideo ?? false,
    height: value.height,
    maxHeight: value.maxHeight,
    title: value.title,
    bgUrl: value.bgUrl ?? '',
    posterUrl: value.posterUrl ?? '',
    fwImageUrl: value.fwImageUrl ?? '',
    blankImageUrl: value.blankImageUrl ?? '',
    link: value.link ?? '',
    shouldRefresh: value.shouldRefresh ?? false,
    productIds: value.productIds ?? [],
    rgbColor: value.rgbColor,
    hexColor: value.hexColor,
    autoplay: value.autoplay,
    playsinline: value.playsinline,
    loop: value.loop,
    muted: value.muted,
    layout: value.layout,
    style: value.style,
  };
}

function toGridSectionAppVm(value: NewHomeSectionValue): GridSectionAppVm {
  return {
    title: value.title,
    showTitle: value.showTitle,
    titleStyle: value.titleStyle,
    layout: value.layout,
    style: value.style,
    gridLayout: value.gridLayout,
    dGridLayout: value.dGridLayout,
    blankImageUrl: value.blankImageUrl,
    items: (value.items ?? []).map(toSectionItemAppVm),
  };
}

function toCarouselSectionAppVm(value: NewHomeSectionValue): CarouselSectionAppVm {
  return {
    title: value.title,
    showTitle: value.showTitle,
    titleStyle: value.titleStyle,
    layout: value.layout,
    style: value.style,
    slideInterval: value.slideInterval,
    blankImageUrl: value.blankImageUrl,
    items: (value.items ?? []).map(toSectionItemAppVm),
    desktopVisibleCount: value.desktopVisibleCount,
    desktopPeekFraction: value.desktopPeekFraction,
    activeBasis: value.activeBasis,
    inactiveBasis: value.inactiveBasis,
    nextBasis: value.nextBasis,
    activeBasisSm: value.activeBasisSm,
    inactiveBasisSm: value.inactiveBasisSm,
    nextBasisSm: value.nextBasisSm,
    activeBasisLg: value.activeBasisLg,
    inactiveBasisLg: value.inactiveBasisLg,
    nextBasisLg: value.nextBasisLg,
    snapOnClick: value.snapOnClick,
    parallaxFactor: value.parallaxFactor,
    parallaxClamp: value.parallaxClamp,
    showParallaxArrowsOnMobile: value.showParallaxArrowsOnMobile,
  };
}

function toRecommendedSectionAppVm(value: NewHomeSectionValue): RecommendedSectionAppVm {
  return {
    title: value.title,
    showTitle: value.showTitle,
    titleStyle: value.titleStyle,
    layout: value.layout,
    style: value.style,
    browseTags: value.browseTags,
    prefetchOffsetSections: value.prefetchOffsetSections,
    categoryFilters: value.categoryFilters,
  };
}

export function toNewHomeSectionAppVm(section: NewHomeSection): NewHomeSectionAppVm {
  const { key, value } = section;

  let appValue: NewHomeSectionAppVm['value'];
  if (key === 'heroVideo') {
    appValue = toHeroValueAppVm(value);
  } else if (key === 'recommended') {
    appValue = toRecommendedSectionAppVm(value);
  } else if (
    value.layout.template === 'carousel' ||
    value.layout.template === 'reels' ||
    value.layout.template === 'parallax-carousel'
  ) {
    appValue = toCarouselSectionAppVm(value);
  } else {
    appValue = toGridSectionAppVm(value);
  }

  return {
    priority: section.priority,
    key: section.key,
    eventLabel: section.eventLabel,
    showInMobile: section.showInMobile,
    showInDesktop: section.showInDesktop,
    value: appValue,
  };
}

export function toNewHomeSectionsAppVm(sections: NewHomeSection[]): NewHomeSectionAppVm[] {
  return sections
    .sort((a, b) => a.priority - b.priority)
    .map(toNewHomeSectionAppVm);
}
